// ProductosListXL - Sprint 44 Fase 3 Vista 3.
// Lista de productos XL. Reemplaza src/modules/productos/list/ProductosList.jsx.
// Mantiene rutas legacy: /productos/create y /productos/:id/edit.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useProductosListXL from './hooks/useProductosListXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import XLBadge from '../../components/xl/XLBadge';
import { fmtMoney } from '../../utils/money';
import { mediaUrl } from './utils';

const CSS_ID = 'xl-prod-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }

.xl-filters {
  display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
}
@media (max-width: 720px) { .xl-filters { grid-template-columns: 1fr } }

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px; align-items: center }

.xl-prod-thumb {
  width: 40px; height: 40px;
  background: var(--xl-color-surface-alt);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  display: inline-flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.xl-prod-thumb img { max-width: 100%; max-height: 100%; object-fit: contain }
.xl-prod-thumb__empty { font-size: 10px; color: var(--xl-color-text-muted) }

.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

const ESTADO_OPTIONS = [
  { value: '',         label: 'Todos los estados' },
  { value: 'activo',   label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
];

export default function ProductosListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const navigate = useNavigate();
  const px = useProductosListXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(px.setQ, 300);

  const categoriaOptions = useMemo(() => ([
    { value: '', label: 'Todas las categorias' },
    ...px.categorias.map((c) => ({ value: c, label: c })),
  ]), [px.categorias]);

  const columns = useMemo(() => ([
    { key: '_thumb', label: '',
      render: (r) => (
        <span className="xl-prod-thumb" aria-hidden="true">
          {r.imagen_thumbnail_url
            ? <img src={mediaUrl(r.imagen_thumbnail_url)} alt="" />
            : <span className="xl-prod-thumb__empty">\u2014</span>}
        </span>
      ) },
    { key: 'codigo', label: 'Codigo',
      render: (r) => <span className="xl-mono">{r.codigo}</span> },
    { key: 'nombre', label: 'Nombre' },
    { key: 'categoria', label: 'Categoria',
      render: (r) => r.categoria || '\u2014' },
    { key: 'precio_venta', label: 'Precio venta', align: 'right',
      render: (r) => (
        <span className="xl-mono">
          {r.precio_venta == null ? '\u2014' : fmtMoney(Number(r.precio_venta), 'COP')}
        </span>
      ) },
    { key: 'costo_ultimo', label: 'Costo ultimo', align: 'right',
      render: (r) => (
        <span className="xl-mono">
          {r.costo_ultimo == null ? '\u2014' : fmtMoney(Number(r.costo_ultimo), 'COP')}
        </span>
      ) },
    { key: 'costo_real', label: 'Costo real', align: 'right',
      render: (r) => (
        <span className="xl-mono">
          {r.costo_real == null ? '\u2014' : fmtMoney(Number(r.costo_real), 'COP')}
        </span>
      ) },
    { key: 'is_active', label: 'Estado',
      render: (r) => (
        <XLBadge variant={r.is_active ? 'success' : 'neutral'} dot>
          {r.is_active ? 'Activo' : 'Inactivo'}
        </XLBadge>
      ) },
  ]), []);

  const { page, pages, total, canPrev, canNext, loading } = px;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Productos</h2>
          <p className="xl-page__subtitle">Catalogo global. Total: {total}</p>
        </div>
        <div>
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={() => navigate('/productos/create')}>
            + Nuevo producto
          </button>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de productos">
        <XLInput
          label="Buscar"
          type="search"
          placeholder="Codigo o nombre..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
        <XLSelect
          label="Categoria"
          value={px.categoria}
          onChange={(e) => px.setCategoria(e.target.value)}
          options={categoriaOptions}
        />
        <XLSelect
          label="Estado"
          value={px.estado}
          onChange={(e) => px.setEstado(e.target.value)}
          options={ESTADO_OPTIONS}
        />
      </section>

      {px.err && (
        <div className="xl-banner xl-banner--err" role="alert">{px.err}</div>
      )}

      <XLTable
        ariaLabel="Lista de productos"
        columns={columns}
        data={px.items}
        loading={loading}
        onRowClick={(r) => navigate('/productos/' + r.id + '/edit')}
        rowKey="id"
        emptyMessage="Sin productos para estos filtros."
      />

      <div className="xl-pager">
        <span>
          {total
            ? ('Pagina ' + page + ' de ' + pages + ' \u00b7 ' + total + ' resultados')
            : 'Sin resultados'}
        </span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canPrev || loading}
            onClick={() => px.setPage(page - 1)}>
            Anterior
          </button>
          <span className="xl-mono">{page} / {pages}</span>
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canNext || loading}
            onClick={() => px.setPage(page + 1)}>
            Siguiente
          </button>
        </span>
      </div>
    </div>
  );
}
